import { useEffect, useState, useCallback, useRef } from "react";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import api, { apiErr } from "@/lib/api";
import { fmtRp, fmtAmount } from "@/components/AmountInput";
import { DateInput, fmtDate } from "@/components/DateInput";
import { fl, srcLabel } from "@/lib/finI18n";
import { coaName } from "@/lib/masterI18n";
import { JournalLines, blankLine, journalTotals, linesPayload, toEditLines } from "@/components/JournalLines";
import { useTableTools, TableToolbar, SelectAllBox, SelectBox, deleteEach } from "@/components/TableTools";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, CheckCircle2, RotateCcw, Upload, Download } from "lucide-react";

const AUTO = ["fixed_asset", "invoice", "production", "invoice_payment", "payroll", "fx_reval"];

export default function Jurnal() {
  const { selectedClientId, clients } = useAuth();
  const { t, lang } = useI18n();
  const clientId = selectedClientId;
  const [entries, setEntries] = useState([]);
  const [coa, setCoa] = useState([]);

  const load = useCallback(async () => {
    if (!clientId) return;
    const [j, c] = await Promise.all([api.get(`/journal-entries?client_id=${clientId}`), api.get(`/coa?client_id=${clientId}`)]);
    setEntries(j.data); setCoa(c.data);
  }, [clientId]);
  useEffect(() => { load(); }, [load]);

  const tools = useTableTools(entries, {
    text: (j) => [j.description, j.reference, j.party, srcLabel(j.source, lang), ...j.lines.map((l) => `${l.coa_code} ${l.coa_name} ${l.ledger || ""}`)].join(" "),
  });
  const post = async (j) => {
    try { await api.post(`/journal-entries/${j.id}/post`); toast.success(j.status === "posted" ? "Jurnal dibatalkan (draft)" : "Jurnal diposting"); load(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const del = async (j) => {
    if (!window.confirm("Hapus jurnal ini?")) return;
    try { await api.delete(`/journal-entries/${j.id}`); toast.success("Jurnal dihapus"); load(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const bulkDel = async (ids) => {
    const own = ids.filter((id) => !AUTO.includes(entries.find((e) => e.id === id)?.source));
    const res = await deleteEach(own, (id) => api.delete(`/journal-entries/${id}`));
    load();
    return { failed: res.failed + ids.length - own.length, message: res.message || (ids.length > own.length ? "jurnal otomatis tidak bisa dihapus" : "") };
  };
  const dk = { en: ["Dr", "Cr"], zh: ["借", "贷"] }[lang] || ["D", "K"];
  const exportCols = [["Tanggal", "date"], ["Referensi", "reference"], ["Keterangan", "description"], ["Sumber", (j) => srcLabel(j.source || "journal", lang)],
    ["Rincian", (j) => j.lines.map((l) => `${l.coa_code} ${coaName({ name: l.coa_name, name_en: l.coa_name_en, name_zh: l.coa_name_zh, code: l.coa_code }, lang)}${l.ledger ? ` [${l.ledger}]` : ""}${l.note ? ` (${l.note})` : ""} ${l.debit ? `${dk[0]} ${fmtAmount(l.debit, lang)}` : `${dk[1]} ${fmtAmount(l.credit, lang)}`}`).join(" | ")],
    ["Debit", "total_debit"], ["Kredit", "total_credit"], ["Status", (j) => (j.status === "posted" ? t("jf.posted") : t("jf.draft"))]];
  const clientName = clients.find((c) => c.id === clientId)?.name || "";

  return (
    <div className="space-y-4" data-testid="journal-page">
      <div className="page-sticky flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-head text-2xl font-bold tracking-tight">{t("journal.title")}</h1>
          <p className="text-sm text-muted-foreground mt-1">{t("journal.desc")} {clientName && `· ${clientName}`}</p>
        </div>
        <JournalForm clientId={clientId} coa={coa} onDone={load} />
      </div>
      <TableToolbar tools={tools} id="journal" cols={exportCols} filename="jurnal_umum" onDelete={bulkDel}>
        <JournalImport clientId={clientId} onDone={load} />
      </TableToolbar>
      <Card className="overflow-hidden">
        <Table>
          <TableHeader><TableRow className="bg-secondary/50">
            <TableHead className="w-8"><SelectAllBox tools={tools} id="journal" /></TableHead>
            <TableHead>{t("common.date")}</TableHead><TableHead>{t("common.description")}</TableHead><TableHead>{t("common.reference")}</TableHead>
            <TableHead className="text-right">{t("common.debit")}</TableHead><TableHead className="text-right">{t("common.credit")}</TableHead>
            <TableHead>{t("common.status")}</TableHead><TableHead className="text-right">{t("common.actions")}</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {tools.view.length === 0 && <TableRow><TableCell colSpan={8} className="text-center py-10 text-muted-foreground">{t("common.noData")}</TableCell></TableRow>}
            {tools.view.map((j) => {
              const auto = AUTO.includes(j.source);
              return (
                <TableRow key={j.id} data-testid={`journal-row-${j.id}`}>
                  <TableCell><SelectBox tools={tools} row={j} id="journal" /></TableCell>
                  <TableCell className="num text-xs whitespace-nowrap">{fmtDate(j.date)}</TableCell>
                  <TableCell className="text-sm">
                    {j.description}
                    <div className="text-[10px] text-muted-foreground">
                      {j.lines.map((l, i) => <span key={i}>{l.coa_code} {coaName({ name: l.coa_name, name_en: l.coa_name_en, name_zh: l.coa_name_zh, code: l.coa_code }, lang)}{l.ledger ? ` [${l.ledger}]` : ""}{l.note ? <em> — {l.note}</em> : null}{i < j.lines.length - 1 ? " · " : ""}</span>)}
                    </div>
                  </TableCell>
                  <TableCell className="text-xs">{j.reference || "—"}</TableCell>
                  <TableCell className="num text-right text-xs">{fmtRp(j.total_debit, lang)}</TableCell>
                  <TableCell className="num text-right text-xs">{fmtRp(j.total_credit, lang)}</TableCell>
                  <TableCell><Badge variant={j.status === "posted" ? "default" : "outline"} className="text-xs">{j.status === "posted" ? t("jf.posted") : t("jf.draft")}</Badge>
                    {auto && <Badge variant="secondary" className="text-[10px] ml-1" data-testid={`journal-auto-${j.id}`}>{fl("auto", lang)} · {srcLabel(j.source, lang)}</Badge>}</TableCell>
                  <TableCell className="text-right whitespace-nowrap">{!auto && <>
                    <Button variant="ghost" size="icon" className="h-7 w-7" title={j.status === "posted" ? "Batalkan Posting" : "Posting"} onClick={() => post(j)} data-testid={`journal-post-${j.id}`}>
                      {j.status === "posted" ? <RotateCcw className="h-4 w-4" /> : <CheckCircle2 className="h-4 w-4 text-emerald-600" />}
                    </Button>
                    {j.status !== "posted" && <JournalForm clientId={clientId} coa={coa} editEntry={j} onDone={load} trigger={<Button variant="ghost" size="icon" className="h-7 w-7" data-testid={`journal-edit-${j.id}`}><Pencil className="h-4 w-4" /></Button>} />}
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => del(j)} data-testid={`journal-del-${j.id}`}><Trash2 className="h-4 w-4" /></Button>
                  </>}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}

function JournalImport({ clientId, onDone }) {
  const { lang } = useI18n();
  const [open, setOpen] = useState(false);
  const [res, setRes] = useState(null);
  const ref = useRef();
  const template = async () => {
    const r = await api.get("/import/journal/template", { responseType: "blob" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(r.data); a.download = "template_jurnal.xlsx"; a.click();
  };
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData(); fd.append("client_id", clientId); fd.append("file", file);
    try {
      const { data } = await api.post("/import/journal", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setRes(data); toast.success(fl("jImported", lang).replace("{n}", data.created)); onDone();
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
    e.target.value = "";
  };
  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setRes(null); }}>
      <DialogTrigger asChild><Button variant="outline" size="sm" className="h-9" data-testid="journal-import-btn"><Upload className="h-4 w-4 mr-1" />{fl("tImport", lang)}</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>{fl("jImportTitle", lang)}</DialogTitle><DialogDescription className="text-xs">{fl("jImportHint", lang)}</DialogDescription></DialogHeader>
        <div className="flex gap-2">
          <Button variant="outline" onClick={template} data-testid="journal-import-template"><Download className="h-4 w-4 mr-1" />Template</Button>
          <Button onClick={() => ref.current?.click()} data-testid="journal-import-upload"><Upload className="h-4 w-4 mr-1" />{fl("tImport", lang)}</Button>
          <input ref={ref} type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={upload} data-testid="journal-import-file" />
        </div>
        {res && (
          <div className="text-xs space-y-1" data-testid="journal-import-result">
            <p className="text-emerald-600">{fl("jImported", lang).replace("{n}", res.created)}</p>
            {res.errors.map((er, i) => <p key={i} className="text-rose-600">{er}</p>)}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function JournalForm({ clientId, coa, editEntry, onDone, trigger }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const blank = () => ({ date: new Date().toISOString().slice(0, 10), description: "", reference: "", lines: [blankLine("dr"), blankLine("cr")] });
  const [f, setF] = useState(blank);

  useEffect(() => {
    if (open) setF(editEntry ? { date: editEntry.date, description: editEntry.description, reference: editEntry.reference, lines: toEditLines(editEntry.lines) } : blank());
  }, [open, editEntry]);
  const { balanced } = journalTotals(f.lines);

  const save = async () => {
    const payload = { client_id: clientId, date: f.date, description: f.description, reference: f.reference, lines: linesPayload(f.lines) };
    try {
      if (editEntry) await api.put(`/journal-entries/${editEntry.id}`, payload);
      else await api.post("/journal-entries", payload);
      toast.success("Jurnal disimpan"); setOpen(false); onDone();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger || <Button data-testid="add-journal-btn"><Plus className="h-4 w-4 mr-2" />{t("journal.new")}</Button>}</DialogTrigger>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-auto">
        <DialogHeader><DialogTitle>{editEntry ? t("common.edit") : t("journal.new")}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="space-y-1.5"><Label>{t("common.date")}</Label><DateInput value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="journal-date" /></div>
            <div className="sm:col-span-2 space-y-1.5"><Label>{t("common.description")}</Label><Input value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} placeholder={t("jf.descPlaceholder")} data-testid="journal-desc" /></div>
          </div>
          <div className="space-y-1.5"><Label>{t("jf.referenceOpt")}</Label><Input value={f.reference} onChange={(e) => setF({ ...f, reference: e.target.value })} data-testid="journal-ref" /></div>
          <JournalLines clientId={clientId} coa={coa} lines={f.lines} onChange={(lines) => setF((s) => ({ ...s, lines }))} testid="journal" />
        </div>
        <DialogFooter><Button onClick={save} disabled={!balanced} data-save="" data-testid="journal-save">{t("common.save")}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
